import {
  RECORDER_FLAG,
  RECORDER_FLAG_VALUE,
  RECORDER_REPLY_EVENT,
  SigningRecorderEvent,
  SigningRecorderPart,
  SigningRecorderReply,
  parseRecorderReply,
  signingEventMessage,
} from '../../common/helpers/signingRecorderEvents'

interface RNWebViewWindow {
  ReactNativeWebView?: { postMessage(message: string): void }
  [RECORDER_FLAG]?: unknown
}

const appWindow = (): RNWebViewWindow => window as unknown as RNWebViewWindow

/**
 * Tells the iOS app's SigningRecorder that a signing part started, Part 1 is
 * done, or the contract was sent. A no-op in a normal browser, where
 * `ReactNativeWebView` does not exist.
 */
export const postSigningEvent = (
  type: SigningRecorderEvent,
  part: SigningRecorderPart | null = null,
): void => {
  appWindow().ReactNativeWebView?.postMessage(signingEventMessage(type, part))
}

/**
 * True only inside an app build that records the signing and answers back
 * (13386544331). A normal browser (the customer's own signing link) and older
 * app builds keep signing as before: there is nothing there that could record.
 */
export const recordingRequired = (): boolean =>
  !!appWindow().ReactNativeWebView &&
  appWindow()[RECORDER_FLAG] === RECORDER_FLAG_VALUE

/**
 * The app waits for the rep to pick audio / no audio, so this is generous; it
 * only exists so a lost reply ends in a message instead of a dead Start.
 */
export const RECORDING_REPLY_TIMEOUT_MS = 120_000

/**
 * Asks the app to start recording this part and resolves with its answer.
 * Signing may only proceed on `{ status: 'recording' }`.
 */
export const requestSigningRecording = (
  part: SigningRecorderPart | null,
): Promise<SigningRecorderReply> =>
  new Promise((resolve) => {
    let timer = 0
    const onReply = (e: Event): void => {
      finish(parseRecorderReply((e as CustomEvent).detail))
    }
    const finish = (reply: SigningRecorderReply): void => {
      window.clearTimeout(timer)
      window.removeEventListener(RECORDER_REPLY_EVENT, onReply)
      resolve(reply)
    }
    window.addEventListener(RECORDER_REPLY_EVENT, onReply)
    timer = window.setTimeout(
      () =>
        finish({
          reason: 'The app did not start the recording in time.',
          status: 'failed',
        }),
      RECORDING_REPLY_TIMEOUT_MS,
    )
    postSigningEvent('signing-start', part)
  })
